import { PropsWithChildren, ReactNode } from 'react';

import { flatten } from 'lodash';

import { ExternalLink } from '@uikit/components/ExternalLink';
import LinkIcon from '@uikit/icons/Link';

import {
  REWARD_POOL_ACCESS_POLICY_TYPE,
  REWARD_POOL_TICKET_CURRENCY,
  RewardPoolAccessPolicyType,
  RewardPoolTicketCurrencyType,
  SelfRewardPoolAccessPolicy,
  SelfRewardPoolAccessPolicyTypeOf,
} from '@shared/api/types/rewardPools';
import { DISCORD_URL } from '@shared/constants';
import { membershipTiers } from '@shared/constants/membershipTiers';
import { GENERAL_URLS, nextProjectUrl } from '@shared/constants/urls';
import { formatList, pluralizeWord } from '@shared/helpers/formatters';
import { formatUsd } from '@shared/helpers/number';

import { Tooltip } from '@ui/uikit/components/common/Tooltip';

const storeBaseUrl = process.env.NEXT_PUBLIC_BASE_PATH || '';

export type RequirementItem = {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  qualified: boolean;
  currencyType?: RewardPoolTicketCurrencyType;
  action?: {
    label: string;
    href: string;
  };
};

export type RewardPoolAccessPolicyToRequirementMapper<P extends string = string> = (
  policy: P extends RewardPoolAccessPolicyType
    ? SelfRewardPoolAccessPolicyTypeOf<P>
    : SelfRewardPoolAccessPolicy,
) => RequirementItem | Array<RequirementItem>;

type RewardPoolAccessPoliciesToRequirementsMapping = {
  [P in RewardPoolAccessPolicyType]: RewardPoolAccessPolicyToRequirementMapper<P>;
};

interface RewardPoolPolicyActionLinkProps {
  href?: string;
}

const RewardPoolPolicyActionLink = ({
  href,
  children,
}: PropsWithChildren<RewardPoolPolicyActionLinkProps>) => {
  return (
    <ExternalLink className="text-base-text-pink inline-flex gap-0.5 items-center" href={href}>
      {children}
    </ExternalLink>
  );
};

const fullDateTimeFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'long',
});

interface RewardPoolPolicyFulfillmentPeriodDateProps {
  date: Date;
  inlcudeYear: boolean;
}

const RewardPoolPolicyFulfillmentPeriodDate = ({
  date,
  inlcudeYear,
}: RewardPoolPolicyFulfillmentPeriodDateProps) => {
  return (
    <Tooltip
      trigger={
        <span className="whitespace-nowrap cursor-pointer border-dashed border-[color:--text-text-quaternary] border-b">
          {date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: inlcudeYear ? 'numeric' : undefined,
          })}
        </span>
      }
      message={fullDateTimeFormat.format(date)}
      contentClassName="whitespace-nowrap"
    />
  );
};

interface RewardPoolPolicyFulfillmentPeriodProps {
  startDate: Date;
  endSate: Date;
}

const RewardPoolPolicyFulfillmentPeriod = ({
  startDate,
  endSate,
}: RewardPoolPolicyFulfillmentPeriodProps) => {
  return (
    <span className="whitespace-nowrap">
      <RewardPoolPolicyFulfillmentPeriodDate
        date={startDate}
        inlcudeYear={startDate.getFullYear() !== endSate.getFullYear()}
      />
      {' and '}
      <RewardPoolPolicyFulfillmentPeriodDate
        date={endSate}
        inlcudeYear={startDate.getFullYear() !== endSate.getFullYear()}
      />
    </span>
  );
};

interface RequirementDescriptionProps {
  text:
    | Exclude<ReactNode, null | undefined>
    | {
        defaultFulfillmentPeriod: Exclude<ReactNode, null | undefined>;
        customFulfillmentPeriod: Exclude<ReactNode, null | undefined>;
      };
  policy: Pick<SelfRewardPoolAccessPolicy, 'start_date' | 'end_date'>;
}

const RequirementDescription = ({ text, policy }: RequirementDescriptionProps) => {
  const policyFulfillmentPeriodStartDate = policy.start_date ? new Date(policy.start_date) : null;
  const policyFulfillmentPeriodEndDate = policy.end_date ? new Date(policy.end_date) : null;

  return (
    // eslint-disable-next-line no-nested-ternary
    policyFulfillmentPeriodStartDate && policyFulfillmentPeriodEndDate ? (
      <>
        {typeof text === 'object' && 'customFulfillmentPeriod' in text
          ? text.customFulfillmentPeriod
          : text}
        {' between '}
        <RewardPoolPolicyFulfillmentPeriod
          startDate={policyFulfillmentPeriodStartDate}
          endSate={policyFulfillmentPeriodEndDate}
        />
      </>
    ) : typeof text === 'object' && 'defaultFulfillmentPeriod' in text ? (
      text.defaultFulfillmentPeriod
    ) : (
      text
    )
  );
};

const REWARD_POOL_ACCESS_POLICIES_TO_REQUIREMENTS_MAPPING: RewardPoolAccessPoliciesToRequirementsMapping =
  {
    [REWARD_POOL_ACCESS_POLICY_TYPE.WHITELIST]: (policy) => {
      return {
        id: 'whitelist',
        title: 'Wallet Whitelist',
        description: policy.description,
        qualified: policy.qualified,
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_CLAIMED]: (policy) => {
      return (policy.hotOffersExpanded || []).map(({ id, name, slug, isClaimed }) => {
        const href = `${storeBaseUrl}/hot-offers/${slug}`;

        return {
          id: `hot-offer-${id}`,
          title: 'Hot Offer Claimed',
          qualified: isClaimed,
          action: {
            label: 'Go to Hot Offer',
            href,
          },
          description: (
            <>
              Successfully claim{' '}
              <ExternalLink
                className="text-base-text-red inline-flex gap-0.5 items-center"
                href={href}
              >
                {name} <LinkIcon className="icon-2xs" />
              </ExternalLink>
            </>
          ),
        };
      });
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.KARMA_LIMIT]: (policy) => {
      return {
        id: `min-karma-${policy.options.minimal_karma}`,
        title: 'Minimum Karma',
        description: (
          <RequirementDescription
            text={`Earn a minimum of ${policy.options.minimal_karma} Karma Points between `}
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Earn Karma',
          href: `${storeBaseUrl}/profile/karma-rewards-guide`,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.MEMBERSHIP_TIERS]: (policy) => {
      const tierNames = policy.options.membership_tiers.map(
        (tierId) => membershipTiers[tierId].name,
      );
      const allTierNamesLabel =
        tierNames.length > 1
          ? `${tierNames.slice(0, -1).join(', ')} or ${tierNames[tierNames.length - 1]}`
          : tierNames[0];

      return {
        id: 'membership',
        title: 'Membership Tier',
        description: `Have ${allTierNamesLabel} Membership`,
        qualified: policy.qualified,
        action: {
          label: 'Magic Membership',
          href: `${storeBaseUrl}/profile/membership/plans`,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_AMOUNT]: (policy) => {
      const claimedAmount = policy.options.claimed_amount || 0;
      const url = `${storeBaseUrl}/hot-offers`;
      return {
        id: 'hot-offers-amount',
        title: 'Hot Offer Claimed in Number - All Time',
        description: (
          <RequirementDescription
            text={
              <>
                Claim a minimum of {claimedAmount}{' '}
                <RewardPoolPolicyActionLink href={url}>
                  Hot Offer{claimedAmount !== 1 ? 's' : ''} <LinkIcon className="icon-2xs" />
                </RewardPoolPolicyActionLink>
              </>
            }
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Hot Offers',
          href: url,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED]: (policy) => {
      return (policy.appsExpanded || []).map(({ id, name, slug }) => {
        const href = `${storeBaseUrl}/app/${slug}/validation`;

        return {
          id: `app-validation-${id}`,
          title: 'Validation - Specific Validation',
          qualified: policy.qualified,
          action: {
            label: 'Go to Validation',
            href,
          },
          description: (
            <>
              Participate in{' '}
              <RewardPoolPolicyActionLink href={href}>
                {name} Validation <LinkIcon className="icon-2xs" />
              </RewardPoolPolicyActionLink>
            </>
          ),
        };
      });
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_SQR]: (policy) => {
      return {
        id: 'staked-sqr',
        title: 'Staking - Currently Staking',
        description: (
          <RequirementDescription
            text={`Stake minimum of ${policy.options.amount} SQR amount`}
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Staking',
          href: GENERAL_URLS.staking,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED_AMOUNT]: (policy) => {
      const href = `${storeBaseUrl}/profile/validation-tasks`;

      return {
        id: 'application-voted-amount',
        title: 'Validation - Number of Validations',
        description: (
          <RequirementDescription
            text={
              <>
                Participate in minimum {policy.options.amount}{' '}
                <RewardPoolPolicyActionLink href={href}>
                  Project {pluralizeWord('Validation', policy.options.amount)}{' '}
                  <LinkIcon className="icon-2xs" />
                </RewardPoolPolicyActionLink>
              </>
            }
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Validations',
          href,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.USER_DAILY_LOGIN_AMOUNT]: (policy) => {
      return {
        id: 'user-daily-logins',
        title: 'Daily Logins',
        description: (
          <RequirementDescription
            text={`Log in daily minimum of ${policy.options.amount} ${pluralizeWord(
              'Time',
              policy.options.amount,
            )}`}
            policy={policy}
          />
        ),
        qualified: policy.qualified,
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.USER_ACCOUNT_AGE]: (policy) => {
      return {
        id: 'user-account-age',
        title: 'Age of Account Magic ID',
        description: (
          <>
            Have Magic ID account minimum {policy.options.daysAmount} of day
            {policy.options.daysAmount !== 1 ? 's' : ''} old
          </>
        ),
        qualified: policy.qualified,
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOKEN_TOTAL]: (policy) => {
      return {
        id: 'swap-token-total',
        title: 'Token-Specific Swap Volume',
        description: (
          <RequirementDescription
            text={`Execute swaps for Token ${policy.options.tokenSymbol?.toUpperCase()} with a minimum total volume of ${
              policy.options.minAmount
            } on Magic Swap`}
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Swap',
          href: process.env.NEXT_PUBLIC_SWAP_HOST || 'https://magicsquare.io/swap',
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOTAL_IN_USD]: (policy) => {
      return {
        id: 'swap-token-total-in-usdt',
        title: 'General Swap Volume',
        description: (
          <RequirementDescription
            text={`Complete token swaps with a cumulative total volume of at least ${formatUsd(
              policy.options.minAmount,
            )} on Magic Swap`}
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Swap',
          href: process.env.NEXT_PUBLIC_SWAP_HOST || 'https://magicsquare.io/swap',
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.USER_DISCORD_ROLE]: (policy) => {
      return {
        id: 'user-discord-role',
        title: 'Discord Role',
        description: (
          <>
            Hold{' '}
            {formatList(
              policy.rolesExpanded.map(({ name }) => name),
              ', ',
              'and',
            )}{' '}
            Discord {pluralizeWord('role', policy.options.rolesIds.length)}
          </>
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Discord',
          href: DISCORD_URL,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQR]: (policy) => {
      return {
        id: 'holding-sqr',
        title: 'Hold SQR',
        description: (
          <RequirementDescription
            text={
              <span>
                {'Hold at least '}
                {policy.options.minAmount}
                {' SQR in your '}
                <RewardPoolPolicyActionLink href={`${storeBaseUrl}/profile/magic-id`}>
                  {'MagicID Balance'}
                </RewardPoolPolicyActionLink>
              </span>
            }
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Buy SQR',
          href: nextProjectUrl.swap('/'),
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRP]: (policy) => {
      return {
        id: 'holding-sqrp',
        title: 'Holding X Amount SQRp',
        description: (
          <RequirementDescription
            text={{
              defaultFulfillmentPeriod: `Hold minimum of ${policy.options.amount} SQRp`,
              customFulfillmentPeriod: `Earn minimum of ${policy.options.amount} SQRp`,
            }}
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Earn SQRp',
          href: GENERAL_URLS.staking,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRV]: (policy) => {
      return {
        id: 'holding-sqrv',
        title: 'Holding X Amount SQRv',
        description: <>Hold minimum of {policy.options.amount} SQRv</>,
        qualified: policy.qualified,
        action: {
          label: 'Earn SQRv',
          href: 'https://docs.magicsquare.io/magic-dao/how-to-get-voting-power-in-the-magic-square-dao',
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_AMOUNT]: (policy) => {
      const href = `${storeBaseUrl}/magic-board`;

      return {
        id: 'pow-offers-amount',
        title: 'Magic Board - Number of Tasks All Time',
        description: (
          <RequirementDescription
            text={
              <>
                Complete a minimum of {policy.options.claimed_amount}{' '}
                <RewardPoolPolicyActionLink href={href}>
                  Magic Board Tasks <LinkIcon className="icon-2xs" />
                </RewardPoolPolicyActionLink>
              </>
            }
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Magic Board',
          href,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_CLAIMED]: (policy) => {
      return (policy.hotOffersExpanded || []).map(({ id, name, slug }) => {
        const href = `${storeBaseUrl}/magic-board/${slug}`;

        return {
          id: `pow-offer-${id}`,
          title: 'Magic Board - Specific Task',
          qualified: policy.qualified,
          action: {
            label: 'Go to Task',
            href,
          },
          description: (
            <>
              Complete the{' '}
              <RewardPoolPolicyActionLink href={href}>
                "{name}" <LinkIcon className="icon-2xs" />
              </RewardPoolPolicyActionLink>{' '}
              Magic Board Task
            </>
          ),
        };
      });
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.REFERRED_AMOUNT]: (policy) => {
      return {
        id: 'referred-amount',
        title: 'Refer a Friend',
        description: (
          <RequirementDescription
            text={`Invite minimum of ${policy.options.amount} friends`}
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Invite Friends',
          href: `${storeBaseUrl}/refer-a-friend`,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.PROJECT_PARTICIPANT]: (policy) => {
      const launchpadHost = process.env.NEXT_PUBLIC_LAUNCHPAD_HOST;
      const projectSlug = policy.project?.slug || '';
      const href = `${launchpadHost}/projects/${projectSlug}`;
      const amount = policy?.options?.amount || 0;

      return {
        id: 'participation-ido',
        title: 'Participate in Specific Token Sale',
        description: (
          <>
            Participate in{' '}
            <RewardPoolPolicyActionLink href={href}>
              {policy.project?.name || 'N/A'} <LinkIcon className="icon-2xs" />
            </RewardPoolPolicyActionLink>{' '}
            {amount > 0 ? `with min deposit ${amount} USDT` : ''}
          </>
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Token Sale',
          href,
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_VOLUME]: (policy) => {
      const launchpadHost = process.env.NEXT_PUBLIC_LAUNCHPAD_HOST;
      const href = launchpadHost;

      return {
        id: 'deposit-volume',
        title: 'Volume in Deposit in All IDOs',
        description: (
          <RequirementDescription
            text={
              <>
                Achieve minimum volume of {policy.options.amount} USDT deposits{' '}
                <RewardPoolPolicyActionLink href={href}>
                  across all IDOs <LinkIcon className="icon-2xs" />
                </RewardPoolPolicyActionLink>
              </>
            }
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Launchpad',
          href: process.env.NEXT_PUBLIC_LAUNCHPAD_HOST || '',
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_TURBO_CHARGE_VOLUME]: (policy) => {
      return {
        id: 'turbo-charge',
        title: 'Used Turbo Charge',
        description: (
          <RequirementDescription
            text={`Use Turbo Charge with minimum volume of ${policy.options.amount}$`}
            policy={policy}
          />
        ),
        qualified: policy.qualified,
        action: {
          label: 'Go to Launchpad',
          href: process.env.NEXT_PUBLIC_LAUNCHPAD_HOST || '',
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.TICKET]: (policy) => {
      return policy.options.currencyTypes.map((currencyType) => {
        const { currency } = currencyType;
        const itemsMap = {
          FREE: {
            title: 'Use Free Raffle Credits',
            description:
              'Get tickets using your free ticket balance. More tickets - higher chance of winning!',
            label: 'Use Free Credits',
          },
          REST: {
            title: `Get Raffle Tickets with ${currency}`,
            description: `Use ${currency} from your Magic ID to enter the raffle.`,
            label: `Get with ${currency}`,
          },
        };

        const { title, description, label } =
          currency === REWARD_POOL_TICKET_CURRENCY.FREE ? itemsMap.FREE : itemsMap.REST;

        return {
          id: `ticket-${currency}`,
          title,
          description,
          qualified: policy.qualified,
          currencyType,
          action: {
            label,
            href: '',
          },
        };
      });
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.LIVENESS_COMPLETED]: (policy) => {
      return {
        id: `liveness-completed`,
        title: 'Complete Humanity Verification',
        description: (
          <span>
            Complete Liveness Verification or Gitcoin Passport in your{' '}
            <ExternalLink
              href={storeBaseUrl + '/profile/magic-id/human-verification'}
              className="text-base-text-red inline-flex gap-0.5 items-center"
            >
              Magic ID
            </ExternalLink>
            {'.'}
          </span>
        ),
        qualified: policy.qualified,
        action: {
          label: 'Complete',
          href: storeBaseUrl + '/profile/magic-id/human-verification',
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.KYC_COMPLETED]: (policy) => {
      return {
        id: `kyc-completed`,
        title: 'Complete KYC Verification',
        description: (
          <span>
            Complete KYC Verification in your{' '}
            <ExternalLink
              href={storeBaseUrl + '/profile/magic-id/kyc-verification'}
              className="text-base-text-red inline-flex gap-0.5 items-center"
            >
              Magic ID
            </ExternalLink>
            {'.'}
          </span>
        ),
        qualified: policy.qualified,
        action: {
          label: 'Complete',
          href: storeBaseUrl + '/profile/magic-id/kyc-verification',
        },
      };
    },

    [REWARD_POOL_ACCESS_POLICY_TYPE.BANXA_BUY_SQR]: (policy) => {
      return {
        id: `banxa-buy-sqr`,
        title: 'Buy SQR via Banxa',
        description: (
          <span>
            Buy SQR tokens with a total value of {policy.options.usdt_amount} USDT or higher via
            Banxa.
          </span>
        ),
        qualified: policy.qualified,
        action: {
          label: 'Buy SQR Tokens',
          href: 'https://magic.banxa.com/?coinType=SQR&blockchain=BSC',
        },
      };
    },
  };

export const mapAccessPoliciesToRequirements = (
  policies: Array<SelfRewardPoolAccessPolicy>,
): Array<RequirementItem> => {
  return flatten(
    policies.map((policy) =>
      REWARD_POOL_ACCESS_POLICIES_TO_REQUIREMENTS_MAPPING[policy.name](policy as never),
    ),
  );
};
